import * as ms from 'mediasoup-client';
import axios from 'axios';
import { useState, useEffect, useMemo } from 'react';
import { EventEmitter } from 'events';

const api = axios.create({ baseURL: '/signal' });

export async function post(cmd: string, payload?: any) {
  const { data } = await api.post(cmd, payload);
  return data;
}

export async function get(cmd: string) {
  const { data } = await api.get(cmd);
  return data;
}

export function p(peer: string, cmd: string) {
  return `/peer/${peer}/${cmd}`;
}

type Connection<T extends 'in' | 'out'> = {
  audio: T extends 'out' ? ms.types.Producer : ms.types.Consumer;
  video?: T extends 'out' ? ms.types.Producer : ms.types.Consumer;
  data: T extends 'out' ? ms.types.DataProducer : ms.types.DataConsumer;
};

type Peers = Record<
  string,
  {
    media: MediaStream;
    sendData: (data: any) => void;
  }
>;
export function useSignaling(id?: string, eventSource?: EventSource) {
  const [signal, setSignal] = useState<Signaling>();
  const [peers, setPeers] = useState<Peers>({});

  useEffect(() => {
    if (!signal) return () => {};
    console.log('ID', signal.getId());
    const listener = (ts: Peers) => {
      const newkeys = new Set(Object.keys(ts));
      Object.keys(peers).forEach((k) => newkeys.delete(k));
      newkeys.forEach((k) => console.log('PEER', k, ts[k].sendData));
      setPeers(ts);
    };
    signal.on('peersChanged', listener);
    return () => signal.off('peersChanged', listener);
  }, [signal]);

  const myVideo = useMemo(() => signal?.getVideo(), [signal]);

  return {
    id: signal?.getId(),
    connectRtc: async () => {
      if (id && eventSource) {
        try {
          const s = await Signaling.create(id, eventSource);
          setSignal(s);
        } catch (e) {
          console.error(`Error creating signal`, e);
        }
      }
    },
    evtSource: signal,
    myVideo,
    peers,
  };
}
export type Signal = ReturnType<typeof useSignaling>;

class Signaling extends EventEmitter {
  private id: string;
  private sendTransport: ms.types.Transport;
  private receiveTransport: ms.types.Transport;
  private eventSource: EventSource;

  private connection: Connection<'out'>;
  private consumers: Record<string, Connection<'in'>>;

  constructor(p: {
    id: string;
    sendTransport: ms.types.Transport;
    receiveTransport: ms.types.Transport;
    audioProducer: ms.types.Producer;
    videoProducer?: ms.types.Producer;
    dataProducer: ms.types.DataProducer;
    eventSource: EventSource;
  }) {
    super();
    this.id = p.id;
    this.sendTransport = p.sendTransport;
    this.receiveTransport = p.receiveTransport;
    this.connection = {
      audio: p.audioProducer,
      video: p.videoProducer,
      data: p.dataProducer,
    };
    this.consumers = {};

    this.eventSource = p.eventSource;
    this.eventSource.addEventListener('message', this.handleEvent.bind(this));
  }

  getId() {
    return this.id;
  }

  private async handleEvent(e: MessageEvent) {
    const { kind, payload } = JSON.parse(e.data);
    switch (kind) {
      case 'newConsumers': {
        const { peer, data, video, audio } = payload;
        const dataConsumer = await this.receiveTransport.consumeData(data);
        dataConsumer.on('message', (e) => this.handleRTCMessage(peer, e));

        this.consumers[peer] = {
          audio: await this.receiveTransport.consume(audio),
          video: video ? await this.receiveTransport.consume(video) : undefined,
          data: dataConsumer,
        };

        this.emit('peersChanged', this.consumersToStreams());

        e.stopPropagation();
        break;
      }
      case 'closeConsumers': {
        const { peer } = payload;
        const { audio, video, data } = this.consumers[peer];
        audio.close();
        video?.close();
        data.close();
        delete this.consumers[peer];

        this.emit('peersChanged', this.consumersToStreams());

        e.stopPropagation();
        break;
      }
      default:
        console.log('unknown event', kind, payload);
    }
  }

  private handleRTCMessage(fromPeer: string, ev: string) {
    const { data, peer: toPeer } = JSON.parse(ev);
    if (toPeer === this.id) {
      // console.log('MSG', fromPeer, toPeer, data);
      this.emit('rtcMessage', { fromPeer, payload: data });
    }
  }

  private consumersToStreams(): Peers {
    return Object.fromEntries(
      Object.entries(this.consumers).map(([peer, { audio, video }]) => {
        const media = new MediaStream();
        media.addTrack(audio.track);
        if (video) media.addTrack(video.track);
        return [
          peer,
          {
            media,
            sendData: (data: any) => this.send(peer, data),
          },
        ];
      }),
    );
  }

  public send(peer: string, data: any) {
    return this.connection.data.send(JSON.stringify({ peer, data }));
  }

  public getVideo() {
    const media = new MediaStream();
    if (this.connection.video?.track)
      media.addTrack(this.connection.video.track);
    return media;
  }

  static async create(id: string, eventSource: EventSource) {
    // TODO add id and eventsource, use stopPropagation on the other eventhandler to prevent some messages from coming here
    // const { id } = await post("connect"); // TODO move this and eventsource out of here
    const routerRtpCapabilities = await get('rtpCaps');

    const device = new ms.Device();
    await device.load({ routerRtpCapabilities });
    await post(p(id, 'caps'), device.rtpCapabilities);

    const payloadStp = await post(p(id, 'createTransport'), {
      kind: 'sendTransport',
      sctpCaps: device.sctpCapabilities,
    });
    const sendTransport = device.createSendTransport(payloadStp);
    const payloadRtp = await post(p(id, 'createTransport'), {
      kind: 'receiveTransport',
      sctpCaps: device.sctpCapabilities,
    });
    const receiveTransport = device.createRecvTransport(payloadRtp);

    const setupTransport = (kind: string, tp: ms.types.Transport) => {
      tp.on('connect', async ({ dtlsParameters }, resolve) => {
        await post(p(id, 'connectTransport'), {
          kind,
          dtlsParameters,
        });
        resolve();
      });
    };
    setupTransport('sendTransport', sendTransport);
    setupTransport('receiveTransport', receiveTransport);

    sendTransport.on(
      'produce',
      async ({ kind, rtpParameters, appData }, resolve) => {
        const resp = await post(p(id, 'produce'), {
          kind,
          rtpParameters,
          appData,
        });
        resolve(resp);
      },
    );
    sendTransport.on(
      'producedata',
      async ({ sctpStreamParameters, label, protocol, appData }, resolve) => {
        const resp = await post(p(id, 'produceData'), {
          sctpStreamParameters,
          label,
          protocol,
          appData,
        });
        resolve(resp);
      },
    );

    const settings = {
      audio: true,
      video:
        device.canProduce('video') &&
        !!(await navigator.mediaDevices
          .enumerateDevices()
          .then((devs) => devs.find((d) => d.kind === 'videoinput'))),
    };
    const stream = await navigator.mediaDevices.getUserMedia(settings);
    const videoProducer = settings.video
      ? await sendTransport.produce({
          track: stream.getVideoTracks()[0],
        })
      : undefined;
    const audioProducer = await sendTransport.produce({
      track: stream.getAudioTracks()[0],
    });
    const dataProducer = await sendTransport.produceData();

    return new Signaling({
      id,
      eventSource,
      sendTransport,
      receiveTransport,
      videoProducer,
      audioProducer,
      dataProducer,
    });
  }
}
